/**
 * GetVariantProperties — the array-as-map property harvest (REQ-CHK-4;
 * research/05 §3) — plus the shared slider display formatting (CPCHQ #13)
 * used by every submitter. Verbatim from v1 checkout_helper.js.
 *
 * all DOM access via the BUNDLED jQuery (DIV-004; v1 used the theme's).
 */
import { appState } from "../state/app-state";
import { CheckNumberInputValidity } from "../elements/build-calculator";
import $ from "jquery";

// A Slider's control value is always a bare number, but the cart line item should
// read the way the shopper saw it on screen — rounded to the element's display
// precision and suffixed with its unit (e.g. "50 cm"). CPCHQ #13. The AdminAPI
// FormulaService strips this suffix before re-pricing (Slider branch in
// FormulaParser), so the formatted value is safe to send through the pricing path.
export function isSliderInput(el: any): boolean {
  return $(el).hasClass("cpc-slider");
}

export function formatSliderValueForCart(el: any): string {
  var $el = $(el);
  var raw = parseFloat($el.val());
  if (isNaN(raw)) return $el.val();
  var maxDecimals = parseInt($el.data("maxdecimals"), 10);
  if (isNaN(maxDecimals)) maxDecimals = 0;
  // Read the raw attribute, not .data(), so a numeric-looking unit (e.g. "2")
  // isn't coerced to a Number by jQuery's data cache.
  var unit = el.getAttribute("data-unit");
  unit = unit == null ? "" : unit.trim();
  var display = raw.toFixed(maxDecimals);
  return unit ? display + " " + unit : display;
}

// Issue #383: jQuery's `:hidden` (display:none / zero rendered size) misses an
// element that's merely moved out of the visible viewport via CSS (e.g.
// `position:absolute; left:-9999px`) — the exact technique used to hide a
// Custom-JS-filled helper number input in the issue's evidence store. A
// bounding-rect-vs-viewport check catches that case regardless of which CSS
// technique produced it (negative offset, a transform, etc.).
export function isOffScreen(element: any): boolean {
  if (typeof element.getBoundingClientRect !== "function") return false;
  var rect = element.getBoundingClientRect();
  var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  return rect.right <= 0 || rect.bottom <= 0 || rect.left >= viewportWidth || rect.top >= viewportHeight;
}

export function GetVariantProperties(): any {
  var props: any = [];
  var lookupProperties: any = {};
  $(".calc-prop").each(function (index: any, element: any) {
    let isHidden = $(element).is(":hidden");
    let isLookupElement = $(element).data("lookup") !== undefined;
    let IsLookupFormulaElement = isLookupElement
      ? $(element).data("isformula") === true
      : false;

    // Issue #383: a number input the shopper can't see or reach (`:hidden`, or
    // moved off-screen with CSS — e.g. a helper field a store's Custom JS
    // writes into) must never run CheckNumberInputValidity. That function has
    // two side effects meant for a shopper who can see and fix the field: it
    // silently drops the property on failure (BUG-027, still exactly preserved
    // for a VISIBLE input) and resets the element's value to `min || 0`
    // (BUG-049, also still exactly preserved for a visible input) — neither is
    // recoverable for a field the shopper never sees. Skipping the check
    // entirely sends the element's raw, untouched value instead.
    let isNotVisibleToShopper = isHidden || isOffScreen(element);

    var elementType = $(element).prop("type");
    if (elementType == "number" && !isNotVisibleToShopper) {
      var result = CheckNumberInputValidity(element);
      if (result !== undefined && !result) {
        appState.isError = true;
        $(element).addClass("error-border");
        return;
      }
    }

    if (isHidden === false || IsLookupFormulaElement === true) {
      var name = $(element).attr("name");
      var value = $(element).val();

      let isCheckBox = $(element).is(":checkbox");
      let isDropdown = $(element).is("select");
      var elementType = $(element).prop("nodeName");
      if ($(element).is(":radio") === true) {
        let isChecked = $(element).is(":checked");
        // continue to next element (jQuery.each treats any non-false return
        // as continue; `return;` is runtime-identical to v1's `return true;`)
        if (isChecked === false) return;
        //Check if this radio button belongs to swatch. Only set the value if its regular radio button
        if ($(element).data("showimage") === undefined) {
          value = $(element).val();
        }
      } else if (elementType === "LABEL") {
        value = $(element).data("val");
      } else if (isCheckBox === true) {
        let isChecked = $(element).is(":checked");
        let checkedValue = $(element).data("checkedvalue");
        let uncheckedValue = $(element).data("uncheckedvalue");
        value = isChecked ? "Checked" : "Unchecked";
      } else if (isDropdown === true) {
        value = $(element).children("option:selected").data("label");
      } else if (isSliderInput(element)) {
        value = formatSliderValueForCart(element);
      } else if ($(element).prop("type") === "number" && !isNotVisibleToShopper) {
        // Issue #ScatterMats: round NumberInputNew values to maxdecimals
        // before writing to cart properties so the order shows "0.7" not
        // "0.7000000000000001". maxdecimals is now stamped as data-maxdecimals
        // on the input by buildNumberInput (matches slider convention).
        // Skipped for a not-visible input (issue #383): `value` above is
        // already its raw, untouched `.val()`, and the merchant's display-
        // decimals setting is a shopper-facing cart-property concern that
        // doesn't apply to a field the shopper never sees — rounding it here
        // would silently throw away precision the pricing formula needs.
        var rawFloat = parseFloat($(element).val());
        if (!isNaN(rawFloat)) {
          var maxDec = parseInt($(element).data("maxdecimals"), 10);
          if (isNaN(maxDec)) maxDec = 0;
          value = rawFloat.toFixed(maxDec);
        }
      }

      name = name!.replace("properties[", "");
      name = name.replace("]", "");
      if (name === "length") {
        name = "Length";
      }

      //For lookup element prepare the data
      if (isLookupElement === true) {
        let lookupelementindex = $(element).data("lookupelementindex");
        let elementIdentifier = $(element).data("identifier");
        if (typeof lookupProperties[elementIdentifier] === "undefined") {
          var lookupElementProcessedElement: any = {
            elementName: elementIdentifier,
            input1: 0,
            input1_label: "",
            input2: 0,
            input2_label: "",
          };

          if (lookupelementindex === 1) {
            lookupElementProcessedElement.input1 = parseFloat($(element).val());
            lookupElementProcessedElement.input1_label = $(element).data("calcname");
          } else if (lookupelementindex === 2) {
            lookupElementProcessedElement.input2 = parseFloat($(element).val());
            lookupElementProcessedElement.input2_label = $(element).data("calcname");
          }

          lookupProperties[elementIdentifier] = lookupElementProcessedElement;
        } else {
          let lookupElementProcessedElement =
            lookupProperties[elementIdentifier];
          if (lookupelementindex === 1) {
            lookupElementProcessedElement.input1 = parseFloat($(element).val());
            lookupElementProcessedElement.input1_label = $(element).data("calcname");
          } else if (lookupelementindex === 2) {
            lookupElementProcessedElement.input2 = parseFloat($(element).val());
            lookupElementProcessedElement.input2_label = $(element).data("calcname");
          }
        }
      } else {
        props[name] = value;
      }
    }
  });

  //Now process file upload elements
  $(".cpc-file-url-input").each(function (index: any, element: any) {
    var name = $(element).attr("name");
    var value = $(element).val();
    if (value && value.trim() !== "") {
      name = name!.replace("properties[", "");
      name = name.replace("]", "");
      props[name] = value;
    }
  });

    //Now process file upload elements
  $(".cpc-photoeditor-url-input").each(function (index: any, element: any) {
    var name = $(element).attr("name");
    var value = $(element).val();
    if (value && value.trim() !== "") {
      name = name!.replace("properties[", "");
      name = name.replace("]", "");
      props[name] = value;
    }
  });

  //Now process lookup element
  for (var key in lookupProperties) {
    props[key] = lookupProperties[key];
  }

  props["shopify_product_price"] = appState.cpc_product_default_price;

  //apply meta fields
  appState.cpc_metaFields.forEach((item: any) => {
    props["shopify_meta_" + item.key] = item.value;
  });

  return props;
}

/**
 * Collects every `properties[X]` entry already on the product form that CPC did not
 * put there itself — another app's field (e.g. Uploadly's uploaded-file link,
 * injected as a hidden input) or a theme field (e.g. a pickup-date picker). On a
 * store using the new checkout flow, `cart_transform_run.js` rebuilds a calculator
 * line's ENTIRE attribute set from `_cpc_properties` alone (lineExpand) and drops
 * everything else the original cart line carried — it has no way to ask Shopify for
 * "every other property" by name. So anything not already inside the blob never
 * reaches the order (Crisp session_42d55424, LiquidDreamsDesign/Uploadly: order
 * #21440's uploaded-file link and the theme's "Scheduled to pickup" field were both
 * silently dropped at checkout).
 *
 * Skips CPC's own inputs: `.calc-prop` and `.cpc-slider` (GetVariantProperties()
 * already covers these, including the lookup/file-upload cases — real sliders
 * always carry both classes together, see `elements/slider.ts`), any `_cpc_`-
 * prefixed name (our own reserved keys), anything carrying a `data-cpc_isquantity`
 * attribute — a Calculation element's hidden companion input
 * (`buildCalculationElement()`, `elements/calculation.ts`) posts under its own
 * `properties[Name]` with no `.calc-prop` class (DIV-008), so the class check
 * alone does not catch it; the isQuantity data attribute is always present on it
 * (`"true"` or `"false"`) and is CPC-exclusive, unlike the class, which only
 * marks the VISIBLE label/span — and anything carrying `data-cpc-temp`, a hidden
 * clone `submit-native.ts` injected earlier THIS SAME submit (App Block
 * properties, translated lookup axes, etc.), which would otherwise double-count
 * a value already in the calculator's own properties, possibly under an
 * already-translated name. Also skips an unchecked radio/checkbox, matching how
 * GetVariantProperties() treats the same input types.
 *
 * Called BEFORE the in-form isQuantity/slider/translation swap-offs so none of
 * those elements have lost their name yet — relying on that ordering instead of
 * racing it keeps the existing `data-cpc-temp` injection order (pinned by
 * goldens) unchanged.
 *
 * Returns a plain map; callers merge it UNDER their own calculator properties (see
 * submit-form-data.ts / submit-native.ts) so a same-named CPC field always wins —
 * this never overrides a calculator value, it only fills in what CPC left empty.
 */
export function collectExternalLineProperties(formm: any): Record<string, string> {
  const props: Record<string, string> = {};
  if (!formm || typeof formm.querySelectorAll !== "function") return props;
  formm.querySelectorAll('[name^="properties["]').forEach(function (input: any) {
    if ($(input).hasClass("calc-prop") || $(input).hasClass("cpc-slider")) return;
    if (input.hasAttribute && input.hasAttribute("data-cpc_isquantity")) return;
    if (input.hasAttribute && input.hasAttribute("data-cpc-temp")) return;
    const rawName: string = input.name || "";
    if (!rawName.startsWith("properties[") || !rawName.endsWith("]")) return;
    const key = rawName.slice("properties[".length, -1);
    if (!key || key.startsWith("_cpc_")) return;
    if ((input.type === "radio" || input.type === "checkbox") && !input.checked) return;
    let value = input.value;
    if (input.tagName === "SELECT") {
      const selected = input.options[input.selectedIndex];
      value = selected ? selected.value : value;
    }
    if (value === undefined || value === null || value === "") return;
    props[key] = value;
  });
  return props;
}

/**
 * Names (in `_cpc_properties` key form) of every element flagged "Use as Quantity".
 *
 * DIV-010: that element's value is posted as the Shopify line quantity, which the shopper can
 * change from the cart stepper. A copy of it as a line attribute can never follow that change —
 * on ACT Foam & Rubber's orders "Quantity: 10" sat next to a line quantity of 9 and the cutting
 * team read the wrong count (Crisp session_6a3a8fb2). So the element is left out of the
 * `_cpc_properties` blob at its two serialisation sites. It stays in `GetVariantProperties()`
 * itself: that map is also the `POST variant` body and formulas reference the element.
 *
 * Reads the flag the way `SubmitFormData` does (jQuery `.data()`, which also sees the
 * `data-cpc_isquantity="true"` attribute) and normalises the name exactly like
 * `GetVariantProperties()` above, so the keys line up.
 */
export function quantityPropertyNames(): string[] {
  var names: string[] = [];
  $(".calc-prop").each(function (_index: any, element: any) {
    if ($(element).data("cpc_isquantity") !== true) return;
    var name = $(element).attr("name");
    if (!name) return;
    name = name.replace("properties[", "");
    name = name.replace("]", "");
    if (name === "length") name = "Length";
    names.push(name);
  });
  return names;
}

/**
 * Per Lookup-element identifier, which axis (`input1`/`input2`) is flagged
 * "isformula" — driven by another element/formula rather than typed in by
 * the shopper. `buildLookupInput()` renders that axis with the `hide-element`
 * CSS class (`!important`, permanently invisible — see `pricing/conditional.ts`'s
 * own comment on that class), independent of any `conditional_display` state
 * the Lookup element also carries. The builder exposes "Is Input 1/2 based on
 * formula?" as two INDEPENDENT toggles, so a Lookup element may mix one
 * formula axis with one ordinary, shopper-typed axis under the same
 * `data-identifier` — only the formula axis is internal.
 *
 * `GetVariantProperties()` deliberately includes a hidden formula axis
 * anyway — `isHidden === false || IsLookupFormulaElement === true` — because
 * its value still has to reach the `/variant` pricing recompute (GVP-003).
 * Both axes fold into ONE nested `{input1_label, input1, input2_label,
 * input2}` object keyed by the shared identifier, and that object rides
 * unfiltered into `_cpc_properties` today. `cart_transform_run.js`
 * unconditionally unwraps any such object into plain, customer-visible line
 * item attributes (`parsedCustomProperties[value.input1_label] = value.input1`)
 * with no way to know an axis was never meant to be seen — a formula-feed
 * value that is supposed to stay as invisible as a Calculation element's
 * hidden companion input leaks onto the Cart Transform component line (issue
 * #83).
 *
 * Reads the flag the way `GetVariantProperties()` does so
 * `stripHiddenLookupFormulaAxes()` can redact only the formula axis per
 * identifier, leaving a mixed lookup's other, customer-facing axis intact.
 */
function hiddenLookupFormulaAxes(): Record<string, { input1?: boolean; input2?: boolean }> {
  var axesByIdentifier: Record<string, { input1?: boolean; input2?: boolean }> = {};
  $(".calc-prop").each(function (_index: any, element: any) {
    if ($(element).data("lookup") === undefined) return;
    if ($(element).data("isformula") !== true) return;
    var identifier = $(element).data("identifier");
    if (!identifier) return;
    var axisIndex = $(element).data("lookupelementindex");
    var entry = axesByIdentifier[identifier] || (axesByIdentifier[identifier] = {});
    if (axisIndex === 1) entry.input1 = true;
    else if (axisIndex === 2) entry.input2 = true;
  });
  return axesByIdentifier;
}

/**
 * Redacts a Lookup element's "isformula" axis/axes from a `_cpc_properties`-
 * bound properties map, IN PLACE (the map passed in must already be a copy
 * the caller owns — both call sites build a fresh filtered object first).
 * Mutates only the identifiers `hiddenLookupFormulaAxes()` flags, and only
 * the flagged axis within each one, so a mixed lookup's other, customer-facing
 * axis survives (issue #83).
 *
 * `cart_transform_run.js`'s unwrap is gated on `value.input1_label` SPECIFICALLY
 * — `if (value && typeof value === 'object' && value.input1_label)` — input2 is
 * only ever emitted from INSIDE that same gate, never on its own. So when the
 * FORMULA axis is input1 and the surviving, customer-facing axis is input2,
 * simply deleting input1/input1_label would leave `{elementName, input2,
 * input2_label}` — a truthy object with no `input1_label` — which CT's gate
 * would never unwrap, stranding it as a raw `[object Object]` line-item value
 * instead of the customer-facing attribute it should become. The surviving
 * axis is therefore always written into the input1 slot (renumbered if it
 * started as input2), which is also what makes a single-survivor object
 * indistinguishable from an ordinary one-more-than-nothing lookup pair as far
 * as CT's own unwrap logic is concerned.
 *
 * If nothing customer-facing is left for an identifier (every axis was a
 * formula axis, or the surviving axis was itself never populated — e.g. also
 * conditionally hidden, so its own label is empty), the identifier is dropped
 * entirely rather than left as a stranded `{elementName}` object.
 */
export function stripHiddenLookupFormulaAxes(
  props: Record<string, unknown>,
): Record<string, unknown> {
  var axesByIdentifier = hiddenLookupFormulaAxes();
  Object.keys(axesByIdentifier).forEach(function (identifier) {
    if (!(identifier in props)) return;
    var value = props[identifier] as any;
    if (!value || typeof value !== "object") return;
    var axes = axesByIdentifier[identifier]!;
    var keepInput1 = !axes.input1 && !!value.input1_label;
    var keepInput2 = !axes.input2 && !!value.input2_label;
    if (!keepInput1 && !keepInput2) {
      delete props[identifier];
      return;
    }
    var redacted: any = { elementName: value.elementName };
    if (keepInput1) {
      redacted.input1_label = value.input1_label;
      redacted.input1 = value.input1;
      if (keepInput2) {
        redacted.input2_label = value.input2_label;
        redacted.input2 = value.input2;
      }
    } else {
      // Only input2 survived — renumber it into the input1 slot so CT's own
      // `value.input1_label` gate actually fires for it.
      redacted.input1_label = value.input2_label;
      redacted.input1 = value.input2;
    }
    props[identifier] = redacted;
  });
  return props;
}
